import { useCallback, useRef, useState } from 'react'
import type { SpeechSession } from '@saip/backend'
import { useBackend } from './context'

export interface UseDictation {
  /** Mic is open, or the recording is being transcribed. */
  active: boolean
  /** Recording has stopped, the text is still on its way. */
  transcribing: boolean
  transcript: string
  /** Last failure (no mic, no model, upload failed), until the next start. */
  error: string | null
  start: () => Promise<void>
  stop: () => Promise<string>
  clearError: () => void
}

/**
 * Drives the dictation overlay. The speech port streams transcript chunks; a
 * batch transcriber (LiteLLM) emits a single final chunk after `stop()`, which
 * is why "transcribing" is its own state.
 */
export function useDictation(onFinal?: (text: string) => void): UseDictation {
  const backend = useBackend()
  const [active, setActive] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const sessionRef = useRef<SpeechSession | null>(null)
  const latestRef = useRef('')

  const start = useCallback(async () => {
    if (active) return
    setActive(true)
    setTranscribing(false)
    setTranscript('')
    setError(null)
    latestRef.current = ''
    try {
      const session = await backend.speech.start()
      sessionRef.current = session
      for await (const chunk of session.transcripts) {
        latestRef.current = chunk.text
        setTranscript(chunk.text)
      }
      if (latestRef.current) onFinal?.(latestRef.current)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Die Spracheingabe ist fehlgeschlagen.'
      setError(message)
    } finally {
      sessionRef.current = null
      setTranscribing(false)
      setActive(false)
    }
  }, [active, backend, onFinal])

  const stop = useCallback(async () => {
    // Keep the overlay up while the recording is transcribed — `start()` clears
    // both flags once the final chunk arrived.
    setTranscribing(true)
    await sessionRef.current?.stop()
    return latestRef.current
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return { active, transcribing, transcript, error, start, stop, clearError }
}
